import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { RootState } from '../../../store/store';
import {
    NotificationMode,
    NotificationSettings,
    NotificationTemplateSwitch,
    useNotificationSettings,
    useUpdateNotificationSettings
} from '../../../services/notificationSettings';

/*
 * Settings > Notifications.
 *
 * Replaces the old static form (two switches and a multi-select that saved nothing). Everything
 * here is saved to the notification service of the environment you are looking at, so dev and
 * production are configured separately.
 */

const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

interface Draft {
    mode: NotificationMode;
    testEmailRecipients: string[];
    testSmsNumber: string;
    birthdayTestEmailRecipients: string[];
    templates: Record<string, boolean>;
}

const toDraft = (settings: NotificationSettings): Draft => ({
    mode: settings.mode,
    testEmailRecipients: settings.testEmailRecipients,
    testSmsNumber: settings.testSmsNumber,
    birthdayTestEmailRecipients: settings.birthdayTestEmailRecipients,
    templates: Object.fromEntries(settings.templates.map(t => [t.key, t.active]))
});

const sameList = (a: string[], b: string[]) => a.length === b.length && a.every((v, i) => v === b[i]);

/** One row per event, with its email and SMS template where each exists. */
interface EventRow {
    event: string;
    label: string;
    description?: string;
    email?: NotificationTemplateSwitch;
    sms?: NotificationTemplateSwitch;
}

const eventRows = (templates: NotificationTemplateSwitch[]): EventRow[] => {
    const rows = new Map<string, EventRow>();
    templates.forEach(template => {
        const isSms = template.type === 'SMS';
        const event = template.key.replace(/_(EMAIL|SMS)$/, '');
        const label = (template.name || event).replace(/\s+(Email|SMS)$/i, '');
        const row = rows.get(event) ?? { event, label };
        if (isSms) row.sms = template; else row.email = template;
        row.description = row.description || template.description;
        rows.set(event, row);
    });
    // Birthday greetings first: they are the ones most often switched on and off.
    return Array.from(rows.values()).sort((a, b) => {
        const birthday = (r: EventRow) => (r.event.includes('BIRTHDAY') ? 0 : 1);
        return birthday(a) - birthday(b) || a.label.localeCompare(b.label);
    });
};

/** A list of email addresses shown as removable chips, with a box to add another. */
const EmailListInput: React.FC<{
    id: string;
    values: string[];
    onChange: (values: string[]) => void;
    placeholder?: string;
}> = ({ id, values, onChange, placeholder }) => {
    const [text, setText] = useState('');
    const [error, setError] = useState<string | undefined>();

    const add = () => {
        const candidates = text.split(/[,;\s]+/).map(v => v.trim().toLowerCase()).filter(Boolean);
        if (candidates.length === 0) return;
        const invalid = candidates.find(v => !EMAIL_PATTERN.test(v));
        if (invalid) {
            setError(`'${invalid}' is not a valid email address`);
            return;
        }
        onChange(Array.from(new Set([...values, ...candidates])));
        setText('');
        setError(undefined);
    };

    return (
        <div>
            <div className="d-flex flex-wrap gap-2 mb-2">
                {values.length === 0 && <span className="text-muted fst-italic small">No addresses</span>}
                {values.map(value => (
                    <span key={value} className="badge rounded-pill bg-light text-dark border d-inline-flex align-items-center px-3 py-2">
                        <i className="fa fa-envelope-o me-2 text-primary"></i>
                        {value}
                        <button
                            type="button"
                            className="btn-close ms-2"
                            style={{ fontSize: '0.55rem' }}
                            aria-label={`Remove ${value}`}
                            onClick={() => onChange(values.filter(v => v !== value))}
                        />
                    </span>
                ))}
            </div>
            <div className="input-group">
                <input
                    id={id}
                    type="email"
                    className={`form-control ${error ? 'is-invalid' : ''}`}
                    placeholder={placeholder ?? 'name@tirhelo.com'}
                    value={text}
                    onChange={e => { setText(e.target.value); setError(undefined); }}
                    onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ',') {
                            e.preventDefault();
                            add();
                        }
                    }}
                />
                <button type="button" className="btn btn-outline-primary" onClick={add} disabled={!text.trim()}>
                    <i className="fa fa-plus me-1"></i>Add
                </button>
                {error && <div className="invalid-feedback">{error}</div>}
            </div>
        </div>
    );
};

/** An on/off switch that says which it is, so the state reads without guessing. */
const SendSwitch: React.FC<{
    template?: NotificationTemplateSwitch;
    on: boolean;
    changed: boolean;
    onToggle: () => void;
}> = ({ template, on, changed, onToggle }) => {
    if (!template) {
        return <span className="text-muted" title="There is no message of this kind for this event">—</span>;
    }
    return (
        <div className="d-inline-flex align-items-center gap-2">
            <div className="form-check form-switch mb-0">
                <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id={`switch-${template.key}`}
                    checked={on}
                    onChange={onToggle}
                    style={{ width: '2.5rem', height: '1.25rem', cursor: 'pointer' }}
                />
            </div>
            <label htmlFor={`switch-${template.key}`} className="mb-0" style={{ cursor: 'pointer' }}>
                <span className={`badge ${on ? 'bg-success' : 'bg-secondary'}`}>
                    {on ? 'Sent' : 'Not sent'}
                </span>
                {changed && <span className="badge bg-warning text-dark ms-1">Unsaved</span>}
            </label>
        </div>
    );
};

const NotificationSettingsPanel: React.FC = () => {
    const user = useSelector((state: RootState) => state.auth.user) as any;
    const { data: settings, isLoading, error, refetch } = useNotificationSettings();
    const updateSettings = useUpdateNotificationSettings();
    const [draft, setDraft] = useState<Draft | null>(null);
    const [confirmLive, setConfirmLive] = useState(false);

    useEffect(() => {
        if (settings) setDraft(toDraft(settings));
    }, [settings]);

    const rows = useMemo(() => eventRows(settings?.templates ?? []), [settings]);

    if (isLoading || (!draft && !error)) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (error || !settings || !draft) {
        return (
            <div className="alert alert-danger d-flex align-items-center justify-content-between">
                <span><i className="fa fa-exclamation-circle me-2"></i>Could not load the notification settings.</span>
                <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => refetch()}>Try again</button>
            </div>
        );
    }

    const saved = toDraft(settings);
    const templateChanged = (key: string) => draft.templates[key] !== saved.templates[key];
    const isDirty = draft.mode !== saved.mode
        || !sameList(draft.testEmailRecipients, saved.testEmailRecipients)
        || draft.testSmsNumber.trim() !== saved.testSmsNumber
        || !sameList(draft.birthdayTestEmailRecipients, saved.birthdayTestEmailRecipients)
        || Object.keys(draft.templates).some(templateChanged);

    const totalTemplates = settings.templates.length;
    const onCount = Object.values(draft.templates).filter(Boolean).length;
    const isTest = draft.mode === 'TEST';

    const setTemplate = (key: string, on: boolean) =>
        setDraft(d => (d ? { ...d, templates: { ...d.templates, [key]: on } } : d));

    const setColumn = (type: 'EMAIL' | 'SMS', on: boolean) =>
        setDraft(d => d ? {
            ...d,
            templates: {
                ...d.templates,
                ...Object.fromEntries(settings.templates.filter(t => t.type === type).map(t => [t.key, on]))
            }
        } : d);

    const save = () => {
        if (draft.testEmailRecipients.length === 0) {
            toast.error('Add at least one test email recipient.');
            return;
        }
        if (!draft.testSmsNumber.trim()) {
            toast.error('Enter the test SMS number.');
            return;
        }
        updateSettings.mutate(
            {
                mode: draft.mode,
                testEmailRecipients: draft.testEmailRecipients,
                testSmsNumber: draft.testSmsNumber.trim(),
                birthdayTestEmailRecipients: draft.birthdayTestEmailRecipients,
                templates: draft.templates,
                changedBy: user?.name || user?.username || user?.email
            },
            {
                onSuccess: () => {
                    setConfirmLive(false);
                    toast.success('Notification settings saved');
                },
                onError: (err: any) => {
                    setConfirmLive(false);
                    toast.error(err?.response?.data?.message || 'Failed to save notification settings');
                }
            }
        );
    };

    // Going live sends to real customers from this environment, so it is confirmed first.
    const handleSave = () => {
        if (draft.mode === 'LIVE' && saved.mode !== 'LIVE') {
            setConfirmLive(true);
        } else {
            save();
        }
    };

    const modeOption = (mode: NotificationMode, icon: string, title: string, text: string) => {
        const selected = draft.mode === mode;
        const colour = mode === 'LIVE' ? 'danger' : 'primary';
        return (
            <div className="col-md-6">
                <button
                    type="button"
                    className={`btn w-100 h-100 text-start p-3 border-2 text-wrap ${selected ? `btn-outline-${colour} active` : 'btn-outline-secondary'}`}
                    style={{ whiteSpace: 'normal' }}
                    onClick={() => setDraft(d => (d ? { ...d, mode } : d))}
                    aria-pressed={selected}
                >
                    <div className="d-flex align-items-start">
                        <i className={`fa ${icon} fa-lg me-3 mt-1`}></i>
                        <div>
                            <div className="fw-bold">{title}</div>
                            <small className={selected ? '' : 'text-muted'}>{text}</small>
                        </div>
                    </div>
                </button>
            </div>
        );
    };

    return (
        <div>
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-1">
                <h5 className="mb-0">Sending notifications</h5>
                <span className={`badge px-3 py-2 ${saved.mode === 'LIVE' ? 'bg-danger' : 'bg-primary'}`}>
                    <i className={`fa ${saved.mode === 'LIVE' ? 'fa-bolt' : 'fa-flask'} me-1`}></i>
                    {saved.mode === 'LIVE' ? 'Live: sending to customers' : 'Test: sending to test recipients'}
                </span>
            </div>
            <p className="text-muted mb-4">
                Choose where messages go and which emails and SMSs are sent. These settings apply to this
                environment only.
            </p>

            {/* Test or live */}
            <div className="card border-0 shadow-sm mb-4">
                <div className="card-body p-4">
                    <h6 className="fw-semibold mb-3">Sending mode</h6>
                    <div className="row g-3">
                        {modeOption('TEST', 'fa-flask', 'Test (dev)',
                            'Every email goes to the test recipients and every SMS to the test number. Customers receive nothing.')}
                        {modeOption('LIVE', 'fa-bolt', 'Live (production)',
                            'Emails and SMSs go to customers and directors. Use only where the customer data is real.')}
                    </div>
                    {draft.mode !== saved.mode && (
                        <div className={`alert ${draft.mode === 'LIVE' ? 'alert-danger' : 'alert-info'} mt-3 mb-0`}>
                            <i className="fa fa-info-circle me-2"></i>
                            {draft.mode === 'LIVE'
                                ? 'After you save, customers will start receiving messages from this environment.'
                                : 'After you save, messages will go to the test recipients instead of customers.'}
                        </div>
                    )}
                </div>
            </div>

            {/* Where test messages go */}
            <div className={`card border-0 shadow-sm mb-4 ${isTest ? '' : 'opacity-75'}`}>
                <div className="card-body p-4">
                    <h6 className="fw-semibold mb-1">Test recipients</h6>
                    <p className="text-muted small mb-4">
                        {isTest
                            ? 'Where messages go while this environment is in Test mode.'
                            : 'Not used in Live mode; kept for when you switch back to Test.'}
                    </p>
                    <div className="row g-4">
                        <div className="col-lg-6">
                            <label htmlFor="testEmailRecipients" className="form-label fw-semibold">All test emails</label>
                            <EmailListInput
                                id="testEmailRecipients"
                                values={draft.testEmailRecipients}
                                onChange={values => setDraft(d => (d ? { ...d, testEmailRecipients: values } : d))}
                            />
                        </div>
                        <div className="col-lg-6">
                            <label htmlFor="testSmsNumber" className="form-label fw-semibold">All test SMSs</label>
                            <input
                                id="testSmsNumber"
                                type="tel"
                                className="form-control"
                                placeholder="+27..."
                                value={draft.testSmsNumber}
                                onChange={e => setDraft(d => (d ? { ...d, testSmsNumber: e.target.value } : d))}
                            />
                        </div>
                        {/* No birthday email recipients: birthday greetings go by SMS only (business, 23 Oct 2026),
                            so in Test mode they go to the test SMS number above. */}
                    </div>
                </div>
            </div>

            {/* Which messages are sent */}
            <div className="card border-0 shadow-sm mb-4">
                <div className="card-header bg-white py-3 px-4 d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div>
                        <h6 className="card-title mb-0 fw-semibold">Messages sent</h6>
                        <small className="text-muted">{onCount} of {totalTemplates} messages are sent</small>
                    </div>
                    <div className="d-flex gap-2 flex-wrap">
                        <button type="button" className="btn btn-sm btn-outline-success" onClick={() => setColumn('EMAIL', true)}>All emails on</button>
                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setColumn('EMAIL', false)}>All emails off</button>
                        <button type="button" className="btn btn-sm btn-outline-success" onClick={() => setColumn('SMS', true)}>All SMSs on</button>
                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setColumn('SMS', false)}>All SMSs off</button>
                    </div>
                </div>
                <div className="card-body p-0">
                    <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th className="px-4 py-3">Event</th>
                                    <th className="py-3"><i className="fa fa-envelope me-2"></i>Email</th>
                                    <th className="py-3"><i className="fa fa-comment me-2"></i>SMS</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map(row => (
                                    <tr key={row.event}>
                                        <td className="px-4">
                                            <div className="fw-semibold">
                                                {row.event.includes('BIRTHDAY') && <i className="fa fa-birthday-cake text-primary me-2"></i>}
                                                {row.label}
                                            </div>
                                            {row.description && <small className="text-muted">{row.description}</small>}
                                        </td>
                                        <td>
                                            <SendSwitch
                                                template={row.email}
                                                on={row.email ? draft.templates[row.email.key] : false}
                                                changed={!!row.email && templateChanged(row.email.key)}
                                                onToggle={() => row.email && setTemplate(row.email.key, !draft.templates[row.email.key])}
                                            />
                                        </td>
                                        <td>
                                            <SendSwitch
                                                template={row.sms}
                                                on={row.sms ? draft.templates[row.sms.key] : false}
                                                changed={!!row.sms && templateChanged(row.sms.key)}
                                                onToggle={() => row.sms && setTemplate(row.sms.key, !draft.templates[row.sms.key])}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div className="d-flex align-items-center gap-3 flex-wrap">
                <button type="button" className="btn btn-primary px-4" onClick={handleSave} disabled={!isDirty || updateSettings.isPending}>
                    {updateSettings.isPending ? (
                        <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Saving...</>
                    ) : (
                        <><i className="fa fa-save me-2"></i>Save Changes</>
                    )}
                </button>
                <button type="button" className="btn btn-outline-secondary" onClick={() => setDraft(saved)} disabled={!isDirty || updateSettings.isPending}>
                    Discard changes
                </button>
                {settings.modifiedDate && (
                    <span className="text-muted small ms-auto">
                        <i className="fa fa-history me-1"></i>
                        Last changed {new Date(settings.modifiedDate).toLocaleString('en-ZA')}
                        {settings.modifiedBy ? ` by ${settings.modifiedBy}` : ''}
                    </span>
                )}
            </div>

            <Modal show={confirmLive} onHide={() => !updateSettings.isPending && setConfirmLive(false)} centered>
                <Modal.Header closeButton={!updateSettings.isPending} className="bg-danger text-white">
                    <Modal.Title className="fw-bold">
                        <i className="fa fa-exclamation-triangle me-2"></i>Switch to Live?
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <p className="mb-2">
                        In Live mode this environment sends emails and SMSs <strong>to customers and directors</strong>:
                        application updates, payment reminders, birthday greetings and the rest of the {onCount} messages
                        switched on.
                    </p>
                    <p className="mb-0 text-muted small">
                        Only do this where the customer data is real. You can switch back to Test at any time.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn btn-outline-secondary" onClick={() => setConfirmLive(false)} disabled={updateSettings.isPending}>
                        Cancel
                    </button>
                    <button type="button" className="btn btn-danger" onClick={save} disabled={updateSettings.isPending}>
                        {updateSettings.isPending ? 'Saving...' : 'Switch to Live and save'}
                    </button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default NotificationSettingsPanel;
